import { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import {
  ArrowLeft,
  Clock,
  MapPin,
  KeySquare,
  FileText,
  Camera,
  Plane,
  PlaneTakeoff,
  Loader2,
  CircleCheck,
  CircleAlert,
  TriangleAlert,
  CalendarDays,
  Route,
  Soup,
  Armchair,
  Monitor,
  ShieldCheck,
  Briefcase,
  NotebookText,
  Stethoscope,
  Pencil,
  Trash2,
} from '@/components/icons'
import { getAirportByIcao } from '@/lib/airport-db'
import { cn } from '../lib/utils'
import { AppLayout } from './AppLayout'
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { AircraftDrawer } from './AircraftDrawer'
import { AircraftActionsSelect } from './AircraftActionsSelect'
import { DevicePasswordsDrawer } from './DevicePasswordsDrawer'
import { AircraftDocumentsDrawer } from './AircraftDocumentsDrawer'
import { AircraftMaintenanceHistoryDrawer } from './AircraftMaintenanceHistoryDrawer'
import { AircraftEquipmentDrawer } from './AircraftEquipmentDrawer'
import { EmptyStateCard } from './EmptyStateCard'
import { OperationsTaskReviewDrawer } from './operations/OperationsTaskReviewDrawer'
import { TaskPickerDrawer } from './TaskPickerDrawer'
import { TaskStatusBadge, type TaskStatus } from './TaskStatusBadge'
import { SwipeableRow } from './ui/swipeable-row'
import {
  useAircraftGetAll,
  useAircraftGetById,
  useAircraftArchive,
  useAircraftUnarchive,
  useAircraftSetPhoto,
  usePlaneSpottersImage,
  useEquipmentGetByAircraft,
} from '../lib/hooks'
import { useTripGetAll } from '../lib/hooks/trip'
import { useSubscriptionStatus } from '../lib/hooks/subscription'
import {
  useS3GetUploadPost,
  useS3DownloadUrlQuery,
  uploadThumbnailBestEffort,
  generateAircraftPhotoS3Key,
} from '@/lib/hooks/s3'
import {
  useTasksByAircraft,
  useTaskStart,
  useTaskDetach,
} from '../lib/hooks/task'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { Card } from '@/components/ui/card'
import { PrntrGroup, PrntrInput, PrntrValue } from '@/components/ui/prntr-input'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { PreferenceCard } from './PAXDetailView'
import { FacilityPhotos } from './FacilityPhotos'
import { Skeleton } from '@/components/ui/skeleton'
import { SectionTitle } from '@/components/ui/section-title'
import { KpiBadge } from '@/components/ui/kpi-badge'
import { useRouter, Link } from '@tanstack/react-router'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { useIsPilot } from '@/hooks/useIsPilot'
import {
  useFlightLogGetByAircraft,
  useFlightLogAircraftStats,
} from '@/lib/hooks/flightLog'
import { formatWholeHours, formatWholeNumber } from '@/lib/flight-log-utils'
import { AircraftStatCard } from './AircraftStatCard'

interface AircraftDetailViewProps {
  aircraftId: string
  initialTab?: 'details' | 'facilities'
}

interface AircraftTaskItem {
  booleanResponse: boolean | null
  textResponse: string | null
  numberResponse: number | null
}

interface AircraftTask {
  id: string
  name: string
  status: string
  items: AircraftTaskItem[]
}

function itemHasResponse(item: AircraftTaskItem): boolean {
  return (
    item.booleanResponse !== null ||
    (item.textResponse !== null && item.textResponse !== '') ||
    item.numberResponse !== null
  )
}

function getDocumentExpiryStatus(
  expiryDate: Date | string
): 'expired' | 'expiring' | 'valid' {
  const now = dayjs()
  const expiry = dayjs(expiryDate)
  if (expiry.isBefore(now)) return 'expired'
  if (expiry.isBefore(now.add(2, 'month'))) return 'expiring'
  return 'valid'
}

function getDocumentsExpiryStatus(
  documents: { expiryDate: Date | string | null }[]
): 'expired' | 'expiring' | 'valid' {
  let hasExpiring = false
  for (const doc of documents) {
    if (!doc.expiryDate) continue
    const status = getDocumentExpiryStatus(doc.expiryDate)
    if (status === 'expired') return 'expired'
    if (status === 'expiring') hasExpiring = true
  }
  return hasExpiring ? 'expiring' : 'valid'
}

export function AircraftDetailView({
  aircraftId,
  initialTab = 'details',
}: AircraftDetailViewProps) {
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [showPasswordsDrawer, setShowPasswordsDrawer] = useState(false)
  const [showDocumentsDrawer, setShowDocumentsDrawer] = useState(false)
  const [showMaintenanceHistory, setShowMaintenanceHistory] = useState(false)
  const [showEquipmentDrawer, setShowEquipmentDrawer] = useState(false)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [isTaskPickerOpen, setIsTaskPickerOpen] = useState(false)
  const [openTaskRowId, setOpenTaskRowId] = useState<string | null>(null)
  const [confirmDeleteTask, setConfirmDeleteTask] =
    useState<AircraftTask | null>(null)

  const { data: aircraft, isPending: loading } = useAircraftGetById(aircraftId)
  const { data: aircraftImageUrl, isPending: imageLoading } =
    usePlaneSpottersImage(aircraft?.registration)
  const { mutate: archiveAircraft } = useAircraftArchive()
  const { mutate: unarchiveAircraft } = useAircraftUnarchive()
  const { navigate: routerNavigate } = useRouter()
  const { isAdmin } = useIsAdmin()
  const { isPilot } = useIsPilot()
  // Flight logs count for the nav card — same query the logs route uses.
  // Admin-or-pilot only; the enabled gate keeps others from calling it.
  // The list is leg-based and includes departed legs with no log yet; only
  // rows with a log number count as "on file".
  const { data: aircraftLegs = [] } = useFlightLogGetByAircraft(aircraftId, {
    enabled: isAdmin || isPilot,
  })
  const aircraftFlightLogs = aircraftLegs.filter((leg) => leg.logNumber !== null)
  // Flight hours / distance totals from this aircraft's submitted logs. The
  // query is company-wide (shared with the aircraft list) and open to every
  // role; an aircraft with no submitted log simply has no row.
  const { data: aircraftStats, isPending: statsPending } =
    useFlightLogAircraftStats()
  const flightStats = aircraftStats?.find((s) => s.aircraftId === aircraftId)
  const { data: tasks } = useTasksByAircraft(aircraftId)
  // Safety equipment count + worst expiry status for the Manage card. Skipped
  // for a deleted aircraft, where getByAircraft 404s.
  const { data: equipment = [] } = useEquipmentGetByAircraft(aircraftId, {
    enabled: !!aircraft && !aircraft.isArchived,
  })
  const startTask = useTaskStart()
  const detachTask = useTaskDetach()
  // Active (non-deleted) aircraft, used to enforce the restore limit.
  const { data: activeAircraft = [] } = useAircraftGetAll()
  const { data: subscription } = useSubscriptionStatus()
  const maxAircraft = subscription?.maxActiveAircraft ?? 3
  const { data: aircraftTrips } = useTripGetAll({ aircraftId })
  const isDesktop = useIsDesktop()

  // Custom photo: an admin-uploaded image takes precedence over the external
  // Planespotters match (e.g. when the registration changed and the external
  // photo is stale or missing). We resolve its presigned URL on demand.
  const setPhoto = useAircraftSetPhoto(aircraftId)
  const getUploadPost = useS3GetUploadPost()
  const photoFileInputRef = useRef<HTMLInputElement>(null)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)

  const photoS3Key = aircraft?.photoS3Key ?? null

  // 'large' = the 1200px variant uploaded next to the original; photos from
  // before variants existed fall back to the original.
  const { data: customPhotoData, isPending: customPhotoPending } = useS3DownloadUrlQuery(
    photoS3Key,
    { variant: 'large' }
  )
  const customPhoto = customPhotoData as
    | { downloadUrl?: string; thumbnailUrl?: string }
    | undefined
  const customPhotoUrl = photoS3Key
    ? (customPhoto?.thumbnailUrl ?? customPhoto?.downloadUrl ?? null)
    : null
  const customPhotoLoading = !!photoS3Key && customPhotoPending

  const handlePickPhoto = () => photoFileInputRef.current?.click()

  const handlePhotoSelected = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image')
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image exceeds the 20 MB limit')
      return
    }

    setIsUploadingPhoto(true)
    try {
      const s3Key = generateAircraftPhotoS3Key(aircraftId, file.name)
      const { url, fields } = await getUploadPost.mutateAsync({
        s3Key,
        contentType: file.type || 'application/octet-stream',
      })

      const formData = new FormData()
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v))
      formData.append('file', file)

      const res = await fetch(url, { method: 'POST', body: formData })
      if (!res.ok) throw new Error(`Upload failed (${res.status})`)

      await uploadThumbnailBestEffort(file, s3Key, getUploadPost.mutateAsync, 'large')

      await setPhoto.mutateAsync({ id: aircraftId, s3Key })
      toast.success('Photo updated')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to upload photo'
      )
    } finally {
      setIsUploadingPhoto(false)
    }
  }

  const handleAddTasks = async (templateIds: string[]) => {
    try {
      await Promise.all(
        templateIds.map((id) =>
          startTask.mutateAsync({ checklistTemplateId: id, aircraftId })
        )
      )
      toast.success(templateIds.length > 1 ? 'Tasks added' : 'Task added')
    } catch {
      toast.error('Failed to add task')
    }
  }

  const openTaskEditor = (taskId: string) => {
    routerNavigate({ to: '/task/$taskId', params: { taskId } })
  }

  const handleTaskEdit = (task: AircraftTask) => {
    setOpenTaskRowId(null)
    openTaskEditor(task.id)
  }

  const handleTaskDeleteRequest = (task: AircraftTask) => {
    setOpenTaskRowId(null)
    setConfirmDeleteTask(task)
  }

  const handleTaskDeleteConfirm = async () => {
    if (!confirmDeleteTask) return
    try {
      await detachTask.mutateAsync({ id: confirmDeleteTask.id })
      toast.success('Task deleted')
      setConfirmDeleteTask(null)
    } catch {
      toast.error('Failed to delete task')
    }
  }

  const handleTaskClick = (task: AircraftTask) => {
    if (task.status === 'Draft') {
      openTaskEditor(task.id)
    } else if (isAdmin) {
      // Only admins review submitted tasks; non-admins see them read-only.
      setSelectedTaskId(task.id)
    }
  }

  const handleArchiveAircraft = async () => {
    if (!aircraft?.id) return

    archiveAircraft({ id: aircraft.id }, {
      onSuccess: () => {
        toast.success('Aircraft archived')
        routerNavigate({ to: '..' })
      },
      onError: (error) => {
        console.error('Error archiving aircraft:', error)
        toast.error('Failed to archive aircraft')
      },
    })
  }

  const handleUnarchiveAircraft = () => {
    if (!aircraft?.id) return

    // An unarchived aircraft rejoins the active fleet, so it counts against the
    // plan's aircraft limit. Surface the same limit dialog the Add flow uses
    // instead of letting the server reject it.
    if (activeAircraft.length >= maxAircraft) {
      setIsLimitDialogOpen(true)
      return
    }

    unarchiveAircraft({ id: aircraft.id }, {
      onSuccess: () => {
        toast.success('Aircraft unarchived')
      },
      onError: (error) => {
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : 'Failed to unarchive aircraft'
        )
      },
    })
  }


  if (loading || !aircraft) {
    const loadingBody = (
      <>
        {/* Hero card */}
        <Skeleton className="h-[236px] w-full rounded-2xl" />

        {/* Flight hours / miles cards */}
        <div className="flex gap-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-[92px] flex-1 rounded-xl" />
          ))}
        </div>

        {/* Maintenance status card */}
        <Card className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="size-5 rounded-full" />
              <div className="flex flex-col gap-1">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <Skeleton className="h-8 w-14 rounded-md" />
          </div>
        </Card>

        {/* Schedule + Assigned Trips cards */}
        <div className="flex gap-3">
          {[0, 1].map((i) => (
            <Card key={i} className="flex-1 flex flex-col gap-3">
              <Skeleton className="size-10 rounded-lg" />
              <div className="flex flex-col gap-1">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-4 w-20" />
              </div>
            </Card>
          ))}
        </div>

        {/* Details card */}
        <Card className="flex flex-col gap-4">
          {[0, 1].map((row) => (
            <div key={row} className="flex gap-4 items-center w-full">
              {[0, 1].map((col) => (
                <div key={col} className="flex-1 flex flex-col gap-1">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-28" />
                </div>
              ))}
            </div>
          ))}
        </Card>

        {/* Manage section */}
        <div className="flex flex-col">
          <SectionTitle label="MANAGE" className="pt-4" />
          <div className="flex flex-col gap-3">
            {[0, 1].map((i) => (
              <Card key={i} className="flex flex-row gap-3 items-center">
                <Skeleton className="size-12 rounded-lg shrink-0" />
                <div className="flex flex-col gap-1 flex-1">
                  <Skeleton className="h-5 w-28" />
                  <Skeleton className="h-4 w-40" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </>
    )

    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader title="Aircraft" />
          {loadingBody}
        </DesktopPage>
      )
    }

    return (
      <AppLayout
        topBar={<TopBarBackIconButton showBackButton={true} />}
        className="pt-4 pb-[calc(var(--safe-bottom)+32px)] flex flex-col gap-4"
      >
        {loadingBody}
      </AppLayout>
    )
  }

  const documents = aircraft.documents ?? []
  const documentsExpiry = getDocumentsExpiryStatus(documents)
  const equipmentExpiry = getDocumentsExpiryStatus(equipment)

  // Group uploaded facility photos by their facility key for the Facilities tab.
  const facilityPhotos =
    (aircraft as
      | { facilityPhotos?: { facility: string; s3Key: string }[] }
      | undefined)?.facilityPhotos ?? []
  const photosByFacility = facilityPhotos.reduce<Record<string, string[]>>(
    (acc, p) => {
      ;(acc[p.facility] ??= []).push(p.s3Key)
      return acc
    },
    {}
  )

  const now = dayjs()

  // Status card: derived from maintenance windows. If the aircraft is in a window
  // right now it reads "Maintenance · Until {end}" (endDate is exclusive); when
  // it isn't, it simply reads "Active" with no subtitle.
  const activeWindow = aircraft.activeMaintenance ?? null
  const inMaintenance = !!activeWindow

  const fmtDay = (d: Date | string) =>
    new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

  // Assigned trips card: upcoming (not-yet-finished) trips for this aircraft.
  // useTripGetAll returns trips sorted earliest-first, so the first upcoming
  // trip is the next one.
  const upcomingTrips = (aircraftTrips ?? []).filter(
    (trip) => !trip.tripEndDate || dayjs(trip.tripEndDate).isAfter(now)
  )
  const upcomingTripCount = upcomingTrips.length
  const nextTripDate = upcomingTrips[0]?.tripStartDate ?? null

  // An admin-uploaded photo wins over the external Planespotters match.
  const displayPhotoUrl = customPhotoUrl ?? aircraftImageUrl ?? null
  // Spinner only while we have nothing to show yet (or an upload is in flight).
  const photoResolving =
    !displayPhotoUrl && (imageLoading || customPhotoLoading || isUploadingPhoto)

  // A soft-deleted aircraft renders read-only: management/navigation affordances
  // are disabled and greyed; the only action offered is restore.
  const isArchived = aircraft.isArchived

  const aircraftActions = isAdmin ? (
    <AircraftActionsSelect
      isArchived={aircraft.isArchived}
      onEdit={() => setIsEditDrawerOpen(true)}
      onArchive={() => setShowArchiveDialog(true)}
      onUnarchive={handleUnarchiveAircraft}
    />
  ) : undefined

  const aircraftBreadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => routerNavigate({ to: '/operations/aircraft' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to aircraft"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/operations" className="hover:underline">
          Operations
        </Link>
        <span className="text-content-primary-subtle"> / </span>
        <Link to="/operations/aircraft" className="hover:underline">
          Aircraft
        </Link>
        <span className="text-content-primary-subtle"> / {aircraft.registration}</span>
      </span>
    </span>
  )

  const detailBody = (
    <>
      {/* Hero Card with Aircraft Image — admins add or change the photo via
          the camera pill in the corner. */}
      <Card className="relative mt-4 h-[236px] shrink-0 flex flex-col items-start justify-end overflow-hidden bg-surface-L2">
        {/* Photo / placeholder / loading layer */}
        <div className="absolute inset-0 flex items-center justify-center">
          {displayPhotoUrl ? (
            <img
              src={displayPhotoUrl}
              alt={aircraft.registration}
              className={cn('size-full object-cover', isArchived && 'grayscale')}
            />
          ) : photoResolving ? (
            <Loader2 className="size-8 animate-spin text-content-primary-subtle" />
          ) : (
            <Plane className="size-12 text-content-primary-subtle" />
          )}
        </div>

        {/* Registration overlay — only legible over an actual photo */}
        {displayPhotoUrl && (
          <div className="relative flex flex-col gap-2">
            <h2 className="font-semibold text-xl text-white tracking-tight">
              {aircraft.registration}
            </h2>
            <p className="text-sm text-white/95 leading-6">
              {aircraft.manufacturer} {aircraft.type}
            </p>
          </div>
        )}

        {/* Add / change affordance (admins) */}
        {isAdmin && !isArchived && !photoResolving && (
          <button
            type="button"
            onClick={handlePickPhoto}
            disabled={isUploadingPhoto}
            className="absolute right-3 bottom-3 z-10 flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm disabled:opacity-50"
          >
            {isUploadingPhoto ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Camera className="size-4" />
            )}
            {displayPhotoUrl ? 'Change' : 'Add photo'}
          </button>
        )}
      </Card>

      {/* Shared hidden file input for the photo upload / change actions */}
      {isAdmin && (
        <input
          ref={photoFileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handlePhotoSelected}
        />
      )}

      {/* Flight hours / miles flown — totals from this aircraft's submitted logs */}
      {statsPending ? (
        <div className="flex gap-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-[92px] flex-1 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="flex gap-3">
          <AircraftStatCard
            label="Flight Hours"
            value={formatWholeHours(flightStats?.totalFlightMinutes ?? 0)}
            icon={<Clock />}
            subtitle="Total"
          />
          <AircraftStatCard
            label="Miles Flown"
            value={formatWholeNumber(flightStats?.totalDistanceNm ?? 0)}
            icon={<MapPin />}
            subtitle="Total"
          />
        </div>
      )}

      {/* Status card — Archived banner, Inactive (neutral), or Active/Maintenance */}
      {isArchived ? (
        <Card className="flex flex-row items-center justify-between gap-3 border border-border-negative-bold/20 bg-content-negative-bold/10 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <CircleAlert className="size-8 shrink-0 text-content-negative-bold" />
            <div className="flex flex-col min-w-0">
              <p className="font-semibold text-sm text-content-negative-bold leading-5">
                Archived
              </p>
              <p className="text-xs text-content-primary-subtle leading-4">
                This aircraft has been archived
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button
              variant="primary"
              size="mini"
              roundness="round"
              className="shrink-0"
              onClick={handleUnarchiveAircraft}
            >
              Unarchive
            </Button>
          )}
        </Card>
      ) : (
        <Card
          className={cn(
            'flex flex-row items-center justify-between gap-3 py-3',
            inMaintenance
              ? 'border border-content-notice-bold/30 bg-background-notice-subtle'
              : 'border border-content-positive-default/30 bg-background-positive-subtle'
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            {inMaintenance ? (
              <TriangleAlert className="size-8 shrink-0 text-background-notice-bold" />
            ) : (
              <CircleCheck className="size-8 shrink-0 text-content-positive-default" />
            )}
            <div className="flex flex-col min-w-0">
              <p className="font-semibold text-sm text-content-primary-bold leading-5">
                {inMaintenance ? 'Maintenance' : 'Active'}
              </p>
              {inMaintenance && (
                <p className="text-xs text-content-primary-subtle leading-4">
                  Until {fmtDay(activeWindow!.endDate)}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="primary"
            size="mini"
            roundness="round"
            className="shrink-0"
            onClick={() => setShowMaintenanceHistory(true)}
          >
            Edit
          </Button>
        </Card>
      )}

      {/* Schedule + Assigned Trips navigation cards — admin-only (they link into
          operations surfaces crew can't reach). */}
      {isAdmin && (
        <div className={cn('flex gap-3', isArchived && 'opacity-70 pointer-events-none')}>
          <Card
            interactive
            onClick={() =>
              routerNavigate({
                to: '/operations/rosters',
                search: { filterAircraftIds: [aircraftId] },
              } as any)
            }
            className="flex-1 flex flex-col gap-3 cursor-pointer text-left"
          >
            <div className="bg-background-brand-subtle p-2 rounded-lg w-fit">
              <CalendarDays className="size-6 text-content-brand-bold" />
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="font-semibold text-base text-content-primary-bold tracking-[-0.5px] leading-6">
                Schedule
              </p>
              <p className="text-sm text-content-primary-subtle">Rosters & duty</p>
            </div>
          </Card>

          <Card
            interactive
            onClick={() =>
              routerNavigate({
                to: '/operations/trips',
                search: { search: aircraft.registration },
              } as any)
            }
            className="flex-1 flex flex-col gap-3 cursor-pointer text-left"
          >
            <div className="flex items-start justify-between">
              <div className="bg-background-brand-subtle p-2 rounded-lg w-fit">
                <Route className="size-6 text-content-brand-bold" />
              </div>
              {upcomingTripCount > 0 && <KpiBadge value={upcomingTripCount} />}
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="font-semibold text-base text-content-primary-bold tracking-[-0.5px] leading-6">
                Assigned Trips
              </p>
              <p className="text-sm text-content-primary-subtle">
                {nextTripDate
                  ? `Next ${new Date(nextTripDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : 'No upcoming trips'}
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Flight Logs card — admins + pilots. Opens the aircraft's log list. */}
      {(isAdmin || isPilot) && (
        <Card
          interactive
          onClick={() =>
            routerNavigate({
              to: '/aircraft/$aircraftId/flight-logs',
              params: { aircraftId },
            } as any)
          }
          className={cn(
            'flex flex-row gap-3 items-center cursor-pointer text-left',
            isArchived && 'opacity-70 pointer-events-none'
          )}
        >
          <div className="bg-background-brand-subtle p-2 rounded-lg shrink-0">
            <PlaneTakeoff className="size-8 text-content-brand-bold" />
          </div>
          <div className="flex flex-col gap-1 items-start flex-1">
            <p className="font-semibold text-base text-content-primary-bold tracking-[-0.5px] leading-6">
              Flight Logs
            </p>
            <p className="text-sm text-content-primary-subtle">
              {aircraftFlightLogs.length === 1
                ? '1 Log on file'
                : `${aircraftFlightLogs.length} Logs on file`}
            </p>
          </div>
          {aircraftFlightLogs.length > 0 && <KpiBadge value={aircraftFlightLogs.length} />}
        </Card>
      )}

      {/* Details / Facilities tabs (hero above stays pinned) */}
      <Tabs
        value={initialTab}
        onValueChange={(value) =>
          routerNavigate({ search: { tab: value }, replace: true } as any)
        }
      >
        <TabsList className="w-full">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="facilities">Facilities</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="flex flex-col gap-4 mt-4">
      {/* Aircraft Details Card */}
      <Card className="px-4 py-1">
        <PrntrGroup>
          <PrntrInput label="Base">
            <PrntrValue
              value={
                aircraft.baseAirportIcao
                  ? `${aircraft.baseAirportIcao} - ${getAirportByIcao(aircraft.baseAirportIcao)?.name || ''}`
                  : null
              }
              placeholder="N/A"
            />
          </PrntrInput>
          <PrntrInput label="Status">
            <PrntrValue value={aircraft.aircraftStatus || 'Active'} />
          </PrntrInput>
          <PrntrInput label="Max Passengers">
            <PrntrValue value={String(aircraft.maxPassengers)} />
          </PrntrInput>
          <PrntrInput label="Crew Capacity">
            <PrntrValue value={String(aircraft.crewCapacity)} />
          </PrntrInput>
        </PrntrGroup>
      </Card>

      {/* Manage Section */}
      <div className="flex flex-col">
        <SectionTitle label="MANAGE" sticky className="pt-4" />
        <div
          className={cn(
            'flex flex-col gap-3',
            isArchived && 'opacity-70 pointer-events-none'
          )}
        >
          {/* Passwords Card */}
          <Card
            interactive
            onClick={() => setShowPasswordsDrawer(true)}
            className="flex flex-row gap-3 items-center cursor-pointer text-left"
          >
            <div className="bg-background-brand-subtle p-2 rounded-lg shrink-0">
              <KeySquare className="size-8 text-content-brand-bold" />
            </div>
            <div className="flex flex-col gap-1 items-start flex-1">
              <p className="font-semibold text-base text-content-primary-bold tracking-[-0.5px] leading-6">
                Passwords
              </p>
              <p className="text-sm text-content-primary-subtle">
                Passwords for aircraft devices
              </p>
            </div>
          </Card>

          {/* Documents Card */}
          <Card
            interactive
            onClick={() => setShowDocumentsDrawer(true)}
            className="flex flex-row gap-3 items-center cursor-pointer text-left"
          >
            <div className="bg-background-brand-subtle p-2 rounded-lg shrink-0">
              <FileText className="size-8 text-content-brand-bold" />
            </div>
            <div className="flex flex-col gap-1 items-start flex-1">
              <p className="font-semibold text-base text-content-primary-bold tracking-[-0.5px] leading-6">
                Documents
              </p>
              <p className="text-sm text-content-primary-subtle">
                SOPs, manuals, and docs
              </p>
            </div>
            {documents.length > 0 && (
              <KpiBadge
                value={documents.length}
                variant={
                  documentsExpiry === 'expired'
                    ? 'error'
                    : documentsExpiry === 'expiring'
                      ? 'warning'
                      : 'none'
                }
              />
            )}
          </Card>

          {/* Safety Equipment Card */}
          <Card
            interactive
            onClick={() => setShowEquipmentDrawer(true)}
            className="flex flex-row gap-3 items-center cursor-pointer text-left"
          >
            <div className="bg-background-brand-subtle p-2 rounded-lg shrink-0">
              <ShieldCheck className="size-8 text-content-brand-bold" />
            </div>
            <div className="flex flex-col gap-1 items-start flex-1">
              <p className="font-semibold text-base text-content-primary-bold tracking-[-0.5px] leading-6">
                Safety Equipment
              </p>
              <p className="text-sm text-content-primary-subtle">
                Equipment and expiry dates
              </p>
            </div>
            {equipment.length > 0 && (
              <KpiBadge
                value={equipment.length}
                variant={
                  equipmentExpiry === 'expired'
                    ? 'error'
                    : equipmentExpiry === 'expiring'
                      ? 'warning'
                      : 'none'
                }
              />
            )}
          </Card>
        </div>
      </div>

      {/* Tasks Section */}
      <div className="flex flex-col">
        <SectionTitle
          label="TASKS"
          count={(tasks as AircraftTask[] | undefined)?.length ?? 0}
          onAdd={isAdmin && !isArchived ? () => setIsTaskPickerOpen(true) : undefined}
          sticky
          className="pt-4"
        />
        {tasks && tasks.length > 0 ? (
          <Card
            className={cn(
              'flex flex-col gap-2 py-3',
              isArchived && 'opacity-70 pointer-events-none'
            )}
          >
            {(tasks as AircraftTask[]).map((task) => {
              const responded = task.items.filter(itemHasResponse).length
              const total = task.items.length
              const isSubmitted = task.status === 'Submitted'
              // Non-admins can't open submitted tasks for review.
              const interactive = !isSubmitted || isAdmin
              return (
                <SwipeableRow
                  key={task.id}
                  isOpen={openTaskRowId === task.id}
                  onOpenChange={(o) => setOpenTaskRowId(o ? task.id : null)}
                  onDelete={() => handleTaskDeleteRequest(task)}
                  onEdit={() => handleTaskEdit(task)}
                  actionLabel="Delete"
                  variant="icon"
                  actionIcon={<Trash2 />}
                  editIcon={<Pencil />}
                  // Deleting (detach) is admin-only on the API.
                  disabled={!isAdmin || isArchived}
                >
                  <button
                    onClick={() => handleTaskClick(task)}
                    disabled={!interactive}
                    className="flex items-center gap-3 w-full text-left rounded-md px-1 py-[7.5px] disabled:cursor-default"
                  >
                    <div className="flex flex-1 flex-col min-w-0">
                      <p className="font-bold text-sm text-content-primary-bold leading-5 truncate">
                        {task.name}
                      </p>
                      <p className="text-xs text-content-primary-subtle leading-4 tracking-[0.18px]">
                        {responded}/{total} Complete
                      </p>
                    </div>
                    <TaskStatusBadge
                      status={task.status as TaskStatus}
                      className="shrink-0"
                    />
                  </button>
                </SwipeableRow>
              )
            })}
          </Card>
        ) : (
          <EmptyStateCard
            label={isAdmin && !isArchived ? 'Add tasks' : 'No tasks'}
            onAdd={isAdmin && !isArchived ? () => setIsTaskPickerOpen(true) : undefined}
          />
        )}
      </div>
        </TabsContent>

        <TabsContent value="facilities" className="flex flex-col gap-4 mt-4">
          <PreferenceCard
            icon={<Soup className="size-4 text-content-brand-bold" />}
            title="Catering & Galley"
            value={aircraft.cateringGalley || '-'}
            photos={
              <FacilityPhotos
                facility="cateringGalley"
                s3Keys={photosByFacility.cateringGalley ?? []}
                readOnly
              />
            }
          />
          <PreferenceCard
            icon={<Armchair className="size-4 text-content-brand-bold" />}
            title="Cabin & Comfort"
            value={aircraft.cabinComfort || '-'}
            photos={
              <FacilityPhotos
                facility="cabinComfort"
                s3Keys={photosByFacility.cabinComfort ?? []}
                readOnly
              />
            }
          />
          <PreferenceCard
            icon={<Monitor className="size-4 text-content-brand-bold" />}
            title="Entertainment & Connectivity"
            value={aircraft.entertainmentConnectivity || '-'}
            photos={
              <FacilityPhotos
                facility="entertainmentConnectivity"
                s3Keys={photosByFacility.entertainmentConnectivity ?? []}
                readOnly
              />
            }
          />
          <PreferenceCard
            icon={<ShieldCheck className="size-4 text-content-brand-bold" />}
            title="Safety & Emergency Equipment"
            value={aircraft.safetyEquipment || '-'}
            photos={
              <FacilityPhotos
                facility="safetyEquipment"
                s3Keys={photosByFacility.safetyEquipment ?? []}
                readOnly
              />
            }
          />
          <PreferenceCard
            icon={<Briefcase className="size-4 text-content-brand-bold" />}
            title="Storage & Supplies"
            value={aircraft.storageSupplies || '-'}
            photos={
              <FacilityPhotos
                facility="storageSupplies"
                s3Keys={photosByFacility.storageSupplies ?? []}
                readOnly
              />
            }
          />
          <PreferenceCard
            icon={<NotebookText className="size-4 text-content-brand-bold" />}
            title="Notes"
            value={aircraft.facilityNotes || '-'}
            photos={
              <FacilityPhotos
                facility="facilityNotes"
                s3Keys={photosByFacility.facilityNotes ?? []}
                readOnly
              />
            }
          />
          <PreferenceCard
            icon={<Stethoscope className="size-4 text-content-brand-bold" />}
            title="Medical Information"
            value={aircraft.medicalInfo || '-'}
            photos={
              <FacilityPhotos
                facility="medicalInfo"
                s3Keys={photosByFacility.medicalInfo ?? []}
                readOnly
              />
            }
          />
        </TabsContent>
      </Tabs>

      {/* Edit Drawer */}
      <AircraftDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        aircraftId={aircraftId}
      />

      {/* Device Passwords + Maintenance History drawers — not mounted for a
          deleted aircraft. They can't be opened (their triggers are disabled),
          and mounting them fires getDevicePasswords / maintenance.getByAircraft,
          which 404 for a soft-deleted aircraft. */}
      {!isArchived && (
        <>
          <DevicePasswordsDrawer
            aircraftId={aircraftId}
            open={showPasswordsDrawer}
            onClose={() => setShowPasswordsDrawer(false)}
          />

          <AircraftMaintenanceHistoryDrawer
            aircraftId={aircraftId}
            open={showMaintenanceHistory}
            onClose={() => setShowMaintenanceHistory(false)}
          />

          <AircraftEquipmentDrawer
            aircraftId={aircraftId}
            open={showEquipmentDrawer}
            onClose={() => setShowEquipmentDrawer(false)}
          />
        </>
      )}

      {/* Documents Drawer */}
      <AircraftDocumentsDrawer
        aircraftId={aircraftId}
        open={showDocumentsDrawer}
        onClose={() => setShowDocumentsDrawer(false)}
      />

      {/* Task Review Drawer */}
      <OperationsTaskReviewDrawer
        taskId={selectedTaskId}
        onOpenChange={(open) => {
          if (!open) setSelectedTaskId(null)
        }}
      />

      {/* Task Picker Drawer (admin: add tasks for this aircraft) */}
      <TaskPickerDrawer
        isOpen={isTaskPickerOpen}
        onClose={() => setIsTaskPickerOpen(false)}
        selectedTemplateIds={[]}
        onSelect={handleAddTasks}
      />

      {/* Task Delete Confirmation Dialog */}
      <AlertDialog
        open={confirmDeleteTask !== null}
        onOpenChange={(o) => {
          if (!o && !detachTask.isPending) setConfirmDeleteTask(null)
        }}
      >
        <AlertDialogContent className="bg-surface-L1">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{confirmDeleteTask?.name}"? Any
              responses and photos on it will be lost. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={detachTask.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleTaskDeleteConfirm()
              }}
              disabled={detachTask.isPending}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              {detachTask.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent className="bg-surface-L1">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Aircraft</AlertDialogTitle>
            <AlertDialogDescription>
              Archive {aircraft.registration}? It will be removed from the active
              fleet and new trips, but existing trips are unaffected. You can
              unarchive it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveAircraft}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Aircraft Limit Dialog — shown when unarchiving would exceed the limit */}
      <AlertDialog open={isLimitDialogOpen} onOpenChange={setIsLimitDialogOpen}>
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>Aircraft Limit Reached</AlertDialogTitle>
            <AlertDialogDescription>
              You've reached the {maxAircraft}-aircraft limit of your plan.
              {'\n\n'}To unarchive this aircraft, please archive an active one,
              upgrade your plan, or contact{' '}
              <a
                href="mailto:support@pronteer.com"
                className="text-content-brand-bold underline"
              >
                support@pronteer.com
              </a>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="primary"
              size="mini"
              className="w-full"
              onClick={() => setIsLimitDialogOpen(false)}
            >
              OK
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title={aircraft.registration}
          breadcrumb={aircraftBreadcrumb}
          action={aircraftActions}
        />
        {detailBody}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title={aircraft.registration}
          rightElement={aircraftActions}
          showBackButton={true}
        />
      }
      className="pb-[calc(var(--safe-bottom)+32px)] flex flex-col gap-4"
    >
      {detailBody}
    </AppLayout>
  )
}
